import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';

import { Spinner } from '../Spinner/Spinner';

import type { ReactNode } from 'react';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<AriaButtonProps, 'children' | 'className' | 'style'> {
  /**
   * primary is the one action the screen exists for: at most one per view.
   * secondary is every other action. ghost is a low-emphasis action inside
   * dense UI (a table row, a toolbar). danger destroys or revokes something.
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** An icon before the label, e.g. `<Plus />` from lucide-react. */
  icon?: ReactNode;
  /** A verb that says what happens: "Scan device", not "OK". */
  children: ReactNode;
}

/**
 * Button triggers an action. For navigation use a link, styled with
 * `buttonClassName` when it has to look like a button.
 *
 * While `isPending` is set the button keeps its width, shows a spinner, stays
 * focusable and announces the pending state — it does not grey out, because a
 * pending action is not an unavailable one.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  isPending,
  ...props
}: ButtonProps) {
  return (
    <AriaButton
      {...props}
      {...(isPending !== undefined ? { isPending } : {})}
      className={buttonClassName}
      data-variant={variant}
      data-size={size}
    >
      {isPending ? <Spinner size="sm" inline /> : icon}
      <span className="rs-button__label">{children}</span>
    </AriaButton>
  );
}

/** Class for a router link that must look like a button. Pair it with
 * data-variant and data-size attributes. */
export const buttonClassName = 'rs-button';
